import React from "react";
import { reportActiveDashboardTutorialMetric } from "../../../utils/dashboardTutorial";
import WebProfitSpinner from "../../../components/system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import MovementDetailDialog from "../../../components/dashboard/erp/MovementDetailDialog";
import MovementFormDialog from "../../../components/dashboard/erp/MovementFormDialog";
import { ErpEmpty, ErpLoading, MovementStatusChip } from "../../../components/dashboard/erp/ErpUi";
import ResponsiveListControls from "../../../components/dashboard/ResponsiveListControls";
import ResponsiveOperationalTable from "../../../components/dashboard/ResponsiveOperationalTable";
import MobileListPagination from "../../../components/dashboard/MobileListPagination";
import {
  ERP_BRAND,
  MOVEMENT_KIND_LABELS,
  entityId,
  formatDateTime,
  formatMoney,
} from "../../../utils/erp";
import { hasAnyProjectPermission, hasProjectPermission } from "../../../utils/projectAccess";
import { isProjectCapabilityEnabled } from "../../../utils/projectCapabilities";

const KIND_OPTIONS = ["sale", "purchase", "expense", "opening_balance", "stock_in", "stock_out", "stock_adjustment", "collection", "payment", "transfer", "cash_in", "cash_out", "reversal"];

// [WP-STABLE][OWNER-HARD-DELETE]
// Estado de prueba: parse JSX verificado; E2E pendiente. Flujo separado de anulación/reversa; confirmación explícita
// antes de invocar el endpoint destructivo owner-only.

function pendingPurchaseReceiptUnits(movement) {
  if (movement?.kind !== "purchase") return 0;
  return (movement.items || []).reduce(
    (sum, item) => sum + (item.stockReceiptStatus === "pending" ? Math.max(0, Number(item.stockDelta || 0)) : 0),
    0,
  );
}

function unidentifiedPurchaseReceiptUnits(movement) {
  if (movement?.kind !== "purchase") return 0;
  return (movement.items || []).reduce(
    (sum, item) => sum + (item.stockReceiptStatus === "unidentified" ? Math.max(0, Number(item.stockDelta || 0)) : 0),
    0,
  );
}

const HISTORICAL_ELIGIBLE_KINDS = new Set(["sale", "purchase", "expense"]);

function canMoveToHistorical(movement) {
  return Boolean(
    movement
      && movement.status === "confirmed"
      && movement.operationalScope !== "historical_reference"
      && HISTORICAL_ELIGIBLE_KINDS.has(movement.kind),
  );
}

function MobileMovementCard({
  movement,
  historicalSelectionMode,
  bulkDeleteSelectionMode,
  selected,
  canUpdate,
  canConfirm,
  canVoid,
  canHardDelete,
  onToggleHistorical,
  onToggleBulkDelete,
  onView,
  onEdit,
  onConfirm,
  onVoid,
  onHardDelete,
}) {
  const historicalEligible = canMoveToHistorical(movement);
  const pendingReceipt = pendingPurchaseReceiptUnits(movement);
  const unidentifiedReceipt = unidentifiedPurchaseReceiptUnits(movement);
  const balanceDue = Number(movement.balanceDue || 0);
  const selectableForBulkDelete = movement.kind !== "reversal" && ["draft", "confirmed", "voided"].includes(movement.status);
  const selectionMode = historicalSelectionMode || bulkDeleteSelectionMode;
  const selectionDisabled = historicalSelectionMode ? !historicalEligible : !selectableForBulkDelete;

  return (
    <Box
      sx={{
        mx: .7,
        my: .7,
        p: 1.15,
        border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
        borderRadius: 2.4,
        bgcolor: selected ? "rgba(243,90,58,.055)" : "var(--wp-panel-surface, #fff)",
        boxShadow: "0 5px 16px rgba(25,36,47,.04)",
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" spacing={.65} alignItems="flex-start" sx={{ minWidth: 0 }}>
            {selectionMode ? (
              <Checkbox
                size="small"
                checked={selected}
                disabled={selectionDisabled}
                onChange={() => (historicalSelectionMode ? onToggleHistorical(entityId(movement)) : onToggleBulkDelete(entityId(movement)))}
                inputProps={{ "aria-label": `Seleccionar ${movement.movementNumber}` }}
                sx={{ p: .25, mt: -.15 }}
              />
            ) : null}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 1000, color: ERP_BRAND.dark, fontSize: ".88rem", lineHeight: 1.1 }}>
                {movement.movementNumber || "Movimiento"}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: .25, color: ERP_BRAND.muted, fontWeight: 750, fontSize: ".66rem" }}>
                {formatDateTime(movement.occurredAt)}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 1000, fontSize: "1rem", lineHeight: 1.1 }}>
              {formatMoney(movement.total, movement.currency)}
            </Typography>
            {balanceDue > 0 ? (
              <Typography variant="caption" sx={{ display: "block", mt: .25, color: "warning.main", fontWeight: 900, fontSize: ".64rem" }}>
                Pendiente {formatMoney(balanceDue, movement.currency)}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap" alignItems="center">
          <Chip size="small" label={MOVEMENT_KIND_LABELS[movement.kind] || movement.kind} sx={{ height: 22, fontWeight: 900, "& .MuiChip-label": { px: .8, fontSize: ".66rem" } }} />
          <MovementStatusChip status={movement.status} />
          {movement.operationalScope === "historical_reference" ? (
            <Chip size="small" icon={<HistoryRoundedIcon />} label="Histórico" variant="outlined" sx={{ height: 22, fontWeight: 900, "& .MuiChip-label": { px: .7, fontSize: ".64rem" }, "& .MuiChip-icon": { fontSize: 14 } }} />
          ) : null}
          {pendingReceipt > 0 ? <Chip size="small" color="warning" variant="outlined" label={`${pendingReceipt} en tránsito`} sx={{ height: 22, "& .MuiChip-label": { px: .7, fontSize: ".64rem" } }} /> : null}
          {unidentifiedReceipt > 0 ? <Chip size="small" color="warning" label={`${unidentifiedReceipt} por identificar`} sx={{ height: 22, "& .MuiChip-label": { px: .7, fontSize: ".64rem" } }} /> : null}
        </Stack>

        {(movement.description || movement.party?.name || movement.sourceNumber) ? (
          <Box sx={{ minWidth: 0 }}>
            {movement.description ? (
              <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 820, fontSize: ".79rem", lineHeight: 1.25, overflowWrap: "anywhere" }}>
                {movement.description}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={.7} useFlexGap flexWrap="wrap" sx={{ mt: .35 }}>
              {movement.party?.name ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 760 }}>{movement.party.name}</Typography> : null}
              {movement.sourceNumber ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Ref. {movement.sourceNumber}</Typography> : null}
            </Stack>
          </Box>
        ) : null}

        <Stack
          direction="row"
          spacing={.65}
          useFlexGap
          flexWrap="wrap"
          sx={{ pt: .9, borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", "& .MuiButton-root": { minHeight: 44 } }}
        >
          <Button size="small" variant="outlined" startIcon={<VisibilityRoundedIcon />} onClick={() => onView(entityId(movement))} sx={{ flex: "1 1 108px", borderRadius: 2, textTransform: "none", fontWeight: 900 }}>
            Ver
          </Button>
          {movement.status === "draft" && canUpdate ? (
            <Button size="small" variant="outlined" startIcon={<EditRoundedIcon />} onClick={() => onEdit(movement)} sx={{ flex: "1 1 108px", borderRadius: 2, textTransform: "none", fontWeight: 900 }}>
              Editar
            </Button>
          ) : null}
          {movement.status === "draft" && canConfirm ? (
            <Button size="small" variant="contained" color="success" startIcon={<CheckCircleRoundedIcon />} onClick={() => onConfirm(movement)} sx={{ flex: "1 1 120px", borderRadius: 2, textTransform: "none", fontWeight: 950 }}>
              Confirmar
            </Button>
          ) : null}
          {movement.status === "confirmed" && movement.operationalScope !== "historical_reference" && canVoid ? (
            <Button size="small" variant="text" color="warning" startIcon={<UndoRoundedIcon />} onClick={() => onVoid(movement)} sx={{ minHeight: 44, px: 1, borderRadius: 2, textTransform: "none", fontWeight: 850 }}>
              Anular
            </Button>
          ) : null}
          {canHardDelete && movement.kind !== "reversal" && ["draft", "confirmed", "voided"].includes(movement.status) ? (
            <Button size="small" variant="text" color="error" startIcon={<DeleteForeverRoundedIcon />} onClick={() => onHardDelete(movement)} sx={{ minHeight: 44, px: 1, borderRadius: 2, textTransform: "none", fontWeight: 900 }}>
              Eliminar definitivamente
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

function MovementFirstUseExperience({
  canCreate,
  canCreateItems,
  canManageCount,
  canManageRegisters,
  bootstrap,
  onCreate,
  onItems,
  onCount,
  onCash,
}) {
  // [WP-STABLE][MOVEMENT-FIRST-USE-ITEMS-PARITY]
  // STATUS: IMPLEMENTED / STATIC_PASS_TARGET / STAGING_PENDING.
  // UX CONTRACT: mismo patrón del estado vacío de Ítems: una decisión principal,
  // texto corto y sin tarjetas de preparación compitiendo por atención.
  const itemCount = Number(bootstrap?.progress?.itemCount ?? bootstrap?.implementationProgress?.totalItems ?? 0);
  const stockTrackedItems = Number(bootstrap?.implementationProgress?.stockTrackedItems ?? 0);
  const stockPendingItems = Number(bootstrap?.implementationProgress?.stockPendingItems ?? 0);
  const registerCount = Array.isArray(bootstrap?.registers) ? bootstrap.registers.length : 0;
  const itemsReady = itemCount > 0;
  const stockReady = itemsReady && (stockTrackedItems === 0 || stockPendingItems === 0);
  const cashReady = registerCount > 0;
  const setupReady = itemsReady && stockReady && cashReady;

  const next = !itemsReady && canCreateItems
    ? { label: "Cargar ítems", onClick: onItems, icon: <CategoryRoundedIcon /> }
    : !stockReady && canManageCount && stockTrackedItems > 0
      ? { label: "Hacer conteo inicial", onClick: onCount, icon: <ChecklistRoundedIcon /> }
      : !cashReady && canManageRegisters
        ? { label: "Configurar cajas", onClick: onCash, icon: <AccountBalanceWalletRoundedIcon /> }
        : canCreate
          ? { label: "Registrar movimiento", onClick: onCreate, icon: <AddRoundedIcon /> }
          : null;

  const status = [
    `Ítems · ${itemsReady ? "listos" : "pendientes"}`,
    `Conteo · ${!itemsReady ? "espera ítems" : stockTrackedItems === 0 ? "no aplica" : stockReady ? "listo" : "pendiente"}`,
    `Cajas · ${cashReady ? "listas" : "pendientes"}`,
  ].join("  ·  ");

  return (
    <Stack alignItems="center" spacing={1.05} sx={{ px: 2, py: { xs: 4.2, sm: 5.2 }, textAlign: "center" }}>
      <Box sx={{ width: 58, height: 58, borderRadius: 3, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.05)", display: "grid", placeItems: "center" }}>
        {next?.icon ? React.cloneElement(next.icon, { sx: { fontSize: 30, color: "rgba(var(--wp-panel-ink-rgb,25,36,47),.68)" } }) : <ReceiptLongRoundedIcon sx={{ fontSize: 30, color: ERP_BRAND.muted }} />}
      </Box>
      <Typography sx={{ fontWeight: 1000, color: ERP_BRAND.dark, fontSize: { xs: "1.05rem", sm: "1.15rem" } }}>
        {setupReady ? "Todo listo para registrar" : "Prepará la base antes del primer movimiento"}
      </Typography>
      <Typography variant="body2" sx={{ maxWidth: 610, color: ERP_BRAND.muted, lineHeight: 1.45 }}>
        Cargá los ítems importantes; si manejan stock, hacé el conteo inicial. Después definí dónde vive el dinero: efectivo, banco o billeteras.
      </Typography>
      <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 800 }}>
        {status}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={0.65} alignItems="center" sx={{ pt: 0.25 }}>
        {next ? (
          <Button variant="contained" startIcon={next.icon} onClick={next.onClick} sx={{ minHeight: 42, borderRadius: 2.2, textTransform: "none", fontWeight: 950 }}>
            {next.label}
          </Button>
        ) : null}
        {!setupReady && canCreate ? (
          <Button variant="text" onClick={onCreate} sx={{ minHeight: 40, textTransform: "none", fontWeight: 900, color: ERP_BRAND.dark }}>
            Continuar igual
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}

export default function ProjectErpMovementsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { project, bootstrap, refreshBootstrap, access } = useOutletContext();
  const canCreate = hasAnyProjectPermission(access, [
    "erp.sales.create",
    "erp.purchases.create",
    "erp.expenses.create",
    "erp.stock.manage",
    "erp.collections.create",
    "erp.payments.create",
    "cash.operations.create",
    "cash.operations.in",
    "cash.operations.out",
  ]);
  const canCreateSale = hasProjectPermission(access, "erp.sales.create");
  const canCreatePurchase = hasProjectPermission(access, "erp.purchases.create");
  const canCreateItems = hasProjectPermission(access, "items.create");
  const canManageInitialCount = hasProjectPermission(access, "erp.stock.manage") && hasProjectPermission(access, "items.read");
  const canManageRegisters = hasProjectPermission(access, "cash.registers.manage");
  const canReadCustomers = hasProjectPermission(access, "customers.read");
  const membershipsEnabled = isProjectCapabilityEnabled(project, "memberships");
  const canUpdate = hasProjectPermission(access, "erp.movements.update");
  const canConfirm = hasProjectPermission(access, "erp.movements.confirm");
  const ownerCanDelete = ["owner", "superadmin"].includes(String(access?.role || "").toLowerCase());
  const canVoid = ownerCanDelete && hasProjectPermission(access, "erp.movements.void");
  const superAdmin = access?.role === "superadmin";
  const [searchParams, setSearchParams] = useSearchParams();
  const salesView = searchParams.get("vista") === "ventas";
  const canCreateInView = salesView ? canCreateSale : canCreate;
  const [movements, setMovements] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(8);
  const [filters, setFilters] = React.useState({
    q: "",
    kind: salesView ? "sale" : "",
    status: "",
    scope: "",
    dateFrom: "",
    dateTo: "",
    sort: "created_desc",
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [formOpen, setFormOpen] = React.useState(searchParams.get("nuevo") === "1");
  // [WP-STABLE][MOVEMENT-QUICK-ENTRY]
  // STATUS: IMPLEMENTED / STATIC_PASS_TARGET / BUILD_PENDING. Estos valores sólo controlan
  // cómo se abre el formulario existente; no cambian persistencia, permisos ni endpoints ERP.
  const [creationLaunch, setCreationLaunch] = React.useState({ mode: "", kind: "" });
  const initialKind = searchParams.get("tipo") || (salesView ? "sale" : "");
  const initialProductId = searchParams.get("itemId") || "";
  const presetQueryKey = searchParams.toString();
  const initialPreset = React.useMemo(() => {
    const presetParams = new URLSearchParams(presetQueryKey);
    if (presetParams.get("origen") !== "gasto-fijo") return null;
    const amount = Number(presetParams.get("monto") || 0);
    return {
      name: presetParams.get("concepto") || "",
      description: presetParams.get("concepto") || "",
      amount: Number.isFinite(amount) ? amount : 0,
      cashRegisterId: presetParams.get("caja") || "",
      expenseAccountCode: presetParams.get("cuenta") || "OTHER_EXPENSE",
      notes: presetParams.get("nota") || "",
    };
  }, [presetQueryKey]);
  const entryContext = searchParams.get("origen") === "catalogo-stock" ? "catalog-stock" : "";
  const [editing, setEditing] = React.useState(null);
  const [detailId, setDetailId] = React.useState(searchParams.get("movementId") || "");
  const [action, setAction] = React.useState(null);
  const [actionReason, setActionReason] = React.useState("");
  const [hardDeleteAccepted, setHardDeleteAccepted] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const [historicalSelectionMode, setHistoricalSelectionMode] = React.useState(false);
  const [selectedHistoricalIds, setSelectedHistoricalIds] = React.useState([]);
  const [historicalDialogOpen, setHistoricalDialogOpen] = React.useState(false);
  const [historicalReason, setHistoricalReason] = React.useState("Migración de comprobantes anteriores al inicio operativo");
  const [historicalProcessing, setHistoricalProcessing] = React.useState(false);
  // [WP-STABLE][OWNER-BULK-HARD-DELETE]
  // Estado de prueba: revisión estructural completada; build/E2E quedan para el entorno del panel. La selección múltiple
  // reutiliza el DELETE owner existente; no agrega rutas ni relaja permisos del backend.
  const [bulkDeleteSelectionMode, setBulkDeleteSelectionMode] = React.useState(false);
  const [selectedDeleteIds, setSelectedDeleteIds] = React.useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = React.useState(false);
  const [bulkDeleteReason, setBulkDeleteReason] = React.useState("");
  const [bulkDeleteAccepted, setBulkDeleteAccepted] = React.useState(false);
  const [bulkDeleteProcessing, setBulkDeleteProcessing] = React.useState(false);
  const [notice, setNotice] = React.useState("");

  React.useEffect(() => {
    setHardDeleteAccepted(false);
    setActionReason("");
  }, [action?.type, action?.movement?._id, action?.movement?.id]);

  React.useEffect(() => {
    // Seguridad UX: la selección destructiva es intencionalmente de la vista actual.
    // Si cambia página, tamaño o filtros, se descarta para evitar borrar registros ocultos.
    setSelectedDeleteIds([]);
    setBulkDeleteDialogOpen(false);
    setBulkDeleteAccepted(false);
  }, [page, rowsPerPage, filters]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listMovements(projectId, {
        page: page + 1,
        pageSize: rowsPerPage,
        ...filters,
      });
      setMovements(data.movements || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message || "No se pudieron cargar los movimientos");
      setMovements([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, filters]);

  React.useEffect(() => {
    const timeout = setTimeout(load, filters.q ? 300 : 0);
    return () => clearTimeout(timeout);
  }, [load, filters.q]);

  React.useEffect(() => {
    if (searchParams.get("nuevo") === "1") setFormOpen(true);
    const requestedMovementId = searchParams.get("movementId") || "";
    if (requestedMovementId) setDetailId(requestedMovementId);
  }, [searchParams]);

  React.useEffect(() => {
    setPage(0);
    setFilters((current) => {
      if (salesView) {
        return current.kind === "sale" ? current : { ...current, kind: "sale" };
      }
      return current.kind === "sale" ? { ...current, kind: "" } : current;
    });
  }, [salesView]);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const hasActiveRefinement = Boolean(
    filters.q.trim()
      || filters.status
      || filters.scope
      || filters.dateFrom
      || filters.dateTo
      || (!salesView && filters.kind)
  );

  const resetListView = () => {
    setPage(0);
    setFilters((current) => ({
      ...current,
      q: "",
      kind: salesView ? "sale" : "",
      status: "",
      scope: "",
      dateFrom: "",
      dateTo: "",
    }));
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setCreationLaunch({ mode: "", kind: "" });
    if (searchParams.has("nuevo") || searchParams.has("tipo") || searchParams.has("itemId") || searchParams.has("origen")) {
      const next = new URLSearchParams(searchParams);
      ["nuevo", "tipo", "itemId", "origen", "concepto", "monto", "caja", "cuenta", "nota"].forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    }
  };

  const closeDetail = () => {
    setDetailId("");
    if (!searchParams.has("movementId")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("movementId");
    setSearchParams(next, { replace: true });
  };

  const performAction = async () => {
    if (!action?.movement) return;
    try {
      setProcessing(true);
      setError("");
      const id = entityId(action.movement);
      if (action.type === "confirm") {
        await erpApi.confirmMovement(projectId, id);
      } else if (action.type === "owner_remove") {
        if (!actionReason.trim() && action.movement?.status !== "draft") {
          setError("Indicá qué se corrigió para dejar trazabilidad.");
          return;
        }
        await erpApi.removeMovementAsOwner(projectId, id, actionReason || "Borrador cargado por error");
        reportActiveDashboardTutorialMetric("owner_movement_removed", {
          movementKind: String(action.movement?.kind || ""),
          previousStatus: String(action.movement?.status || ""),
          preservedAudit: action.movement?.status !== "draft",
        });
      } else if (action.type === "hard_delete") {
        if (!hardDeleteAccepted) {
          setError("Confirmá que entendés que la eliminación definitiva no se puede deshacer.");
          return;
        }
        if (!actionReason.trim() && action.movement?.status !== "draft") {
          setError("Indicá brevemente qué se cargó mal.");
          return;
        }
        const deleted = await erpApi.hardDeleteMovementAsOwner(projectId, id, actionReason || "Borrador cargado por error");
        reportActiveDashboardTutorialMetric("owner_movement_hard_deleted", {
          movementKind: String(action.movement?.kind || ""),
          previousStatus: String(action.movement?.status || ""),
          removedMovements: Number(deleted?.removedCounts?.erpMovements || 0),
          irreversible: true,
        });
      } else {
        if (!actionReason.trim()) {
          setError("Indicá el motivo de la anulación.");
          return;
        }
        await erpApi.voidMovement(projectId, id, actionReason);
      }
      setAction(null);
      setActionReason("");
      await Promise.all([load(), refreshBootstrap()]);
    } catch (err) {
      setError(err.message || "No se pudo procesar el movimiento");
    } finally {
      setProcessing(false);
    }
  };

  const eligibleHistoricalOnPage = movements.filter(canMoveToHistorical);
  const selectedHistoricalSet = new Set(selectedHistoricalIds);
  const selectedEligibleOnPage = eligibleHistoricalOnPage.filter((movement) => selectedHistoricalSet.has(entityId(movement)));
  const allEligibleOnPageSelected = eligibleHistoricalOnPage.length > 0 && selectedEligibleOnPage.length === eligibleHistoricalOnPage.length;

  const toggleHistoricalSelection = (movementId) => {
    setSelectedHistoricalIds((current) => (
      current.includes(movementId)
        ? current.filter((id) => id !== movementId)
        : [...current, movementId]
    ));
  };

  const toggleHistoricalPage = () => {
    const pageIds = eligibleHistoricalOnPage.map(entityId);
    setSelectedHistoricalIds((current) => {
      if (allEligibleOnPageSelected) return current.filter((id) => !pageIds.includes(id));
      return [...new Set([...current, ...pageIds])];
    });
  };

  const exitHistoricalSelection = () => {
    setHistoricalSelectionMode(false);
    setSelectedHistoricalIds([]);
  };

  const migrateSelectedHistorical = async () => {
    if (!selectedHistoricalIds.length) return;
    try {
      setHistoricalProcessing(true);
      setError("");
      setNotice("");
      const result = await erpApi.markMovementsHistorical(projectId, selectedHistoricalIds, historicalReason);
      setHistoricalDialogOpen(false);
      exitHistoricalSelection();
      setNotice(result?.message || "Los movimientos seleccionados pasaron a histórico.");
      await Promise.all([load(), refreshBootstrap()]);
    } catch (err) {
      setError(err.message || "No se pudieron pasar los movimientos a histórico");
    } finally {
      setHistoricalProcessing(false);
    }
  };

  const eligibleDeleteOnPage = movements.filter((movement) => (
    movement.kind !== "reversal" && ["draft", "confirmed", "voided"].includes(movement.status)
  ));
  const selectedDeleteSet = new Set(selectedDeleteIds);
  const selectedDeleteOnPage = eligibleDeleteOnPage.filter((movement) => selectedDeleteSet.has(entityId(movement)));
  const allDeleteOnPageSelected = eligibleDeleteOnPage.length > 0 && selectedDeleteOnPage.length === eligibleDeleteOnPage.length;

  const toggleBulkDeleteSelection = (movementId) => {
    setSelectedDeleteIds((current) => (
      current.includes(movementId)
        ? current.filter((id) => id !== movementId)
        : [...current, movementId]
    ));
  };

  const toggleBulkDeletePage = () => {
    const pageIds = eligibleDeleteOnPage.map(entityId);
    setSelectedDeleteIds((current) => {
      if (allDeleteOnPageSelected) return current.filter((id) => !pageIds.includes(id));
      return [...new Set([...current, ...pageIds])];
    });
  };

  const exitBulkDeleteSelection = () => {
    setBulkDeleteSelectionMode(false);
    setSelectedDeleteIds([]);
    setBulkDeleteDialogOpen(false);
    setBulkDeleteReason("");
    setBulkDeleteAccepted(false);
  };

  const hardDeleteSelectedMovements = async () => {
    if (!selectedDeleteIds.length || !bulkDeleteAccepted) return;
    if (!bulkDeleteReason.trim()) {
      setError("Indicá brevemente por qué eliminás estos movimientos.");
      return;
    }

    try {
      setBulkDeleteProcessing(true);
      setError("");
      setNotice("");

      const deletedIds = [];
      const failures = [];
      // Se ejecuta en serie de forma intencional: evita competir por stock/caja/sesiones
      // y permite conservar los IDs que el backend bloquee por reglas fiscales u operativas.
      for (const movementId of selectedDeleteIds) {
        try {
          await erpApi.hardDeleteMovementAsOwner(projectId, movementId, bulkDeleteReason.trim());
          deletedIds.push(movementId);
        } catch (err) {
          failures.push({ movementId, message: err?.message || "No se pudo eliminar" });
        }
      }

      reportActiveDashboardTutorialMetric("owner_movements_bulk_hard_deleted", {
        requested: selectedDeleteIds.length,
        deleted: deletedIds.length,
        failed: failures.length,
        irreversible: true,
      });

      if (failures.length) {
        const failedIds = failures.map((item) => item.movementId);
        setSelectedDeleteIds(failedIds);
        setBulkDeleteDialogOpen(false);
        setBulkDeleteAccepted(false);
        setNotice(deletedIds.length ? `Se eliminaron ${deletedIds.length} movimiento${deletedIds.length === 1 ? "" : "s"}.` : "");
        setError(`${failures.length} movimiento${failures.length === 1 ? " quedó" : "s quedaron"} sin eliminar. ${failures[0].message}`);
      } else {
        const count = deletedIds.length;
        exitBulkDeleteSelection();
        setNotice(`Se eliminaron definitivamente ${count} movimiento${count === 1 ? "" : "s"}.`);
      }

      await Promise.all([load(), refreshBootstrap()]);
    } catch (err) {
      setError(err?.message || "No se pudo completar la eliminación múltiple");
    } finally {
      setBulkDeleteProcessing(false);
    }
  };

  const firstMovementEmpty = !salesView && !loading && !error && !movements.length && !hasActiveRefinement;

  return (
    <Stack
      spacing={0}
      sx={{
        pb: 0,
        mx: { xs: "-12px", sm: "-20px", lg: "-24px" },
        mt: "-10px",
      }}
    >
      {error ? <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 3 }}>{error}</Alert> : null}
      {notice ? <Alert severity="success" onClose={() => setNotice("")} sx={{ borderRadius: 3 }}>{notice}</Alert> : null}

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: 0,
          border: 0,
          borderTop: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.09)",
          borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.09)",
          overflow: "hidden",
          bgcolor: "var(--wp-panel-surface,#fff)",
          boxShadow: "none",
        }}
      >
        {!firstMovementEmpty ? (
          <Box sx={{ px: { xs: 0.8, md: 1 }, py: 0.75, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
            <ResponsiveListControls
              search={(
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Buscar movimiento, referencia o tercero"
                  value={filters.q}
                  onChange={(event) => setFilter("q", event.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { minHeight: 40, borderRadius: 2.2, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.025)" } }}
                />
              )}
              actions={(
                <Stack direction="row" spacing={0.55} alignItems="center" useFlexGap flexWrap={{ xs: "wrap", md: "nowrap" }} justifyContent={{ xs: "space-between", md: "flex-start" }}>
                  <Tooltip title="Actualizar">
                    <IconButton size="small" onClick={load} sx={{ width: 36, height: 36, border: `1px solid ${ERP_BRAND.border}`, borderRadius: 2 }}>
                      <RefreshRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {superAdmin && !salesView ? (
                    <>
                      {historicalSelectionMode && selectedHistoricalIds.length ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          startIcon={<HistoryRoundedIcon fontSize="small" />}
                          onClick={() => setHistoricalDialogOpen(true)}
                          sx={{ minHeight: 36, borderRadius: 2, px: 1.2, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}
                        >
                          Pasar a histórico ({selectedHistoricalIds.length})
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        variant={historicalSelectionMode ? "contained" : "outlined"}
                        startIcon={<HistoryRoundedIcon fontSize="small" />}
                        onClick={() => {
                          if (historicalSelectionMode) exitHistoricalSelection();
                          else {
                            exitBulkDeleteSelection();
                            setHistoricalSelectionMode(true);
                          }
                        }}
                        sx={{ minHeight: 36, borderRadius: 2, px: 1.1, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}
                      >
                        {historicalSelectionMode ? "Cancelar selección" : "Seleccionar movimientos"}
                      </Button>
                    </>
                  ) : null}
                  {ownerCanDelete ? (
                    <>
                      {bulkDeleteSelectionMode && selectedDeleteIds.length ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<DeleteForeverRoundedIcon fontSize="small" />}
                          onClick={() => setBulkDeleteDialogOpen(true)}
                          sx={{ minHeight: 36, borderRadius: 2, px: 1.2, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}
                        >
                          Eliminar seleccionados ({selectedDeleteIds.length})
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        variant={bulkDeleteSelectionMode ? "contained" : "outlined"}
                        color={bulkDeleteSelectionMode ? "error" : "inherit"}
                        startIcon={<DeleteForeverRoundedIcon fontSize="small" />}
                        onClick={() => {
                          if (bulkDeleteSelectionMode) exitBulkDeleteSelection();
                          else {
                            exitHistoricalSelection();
                            setBulkDeleteSelectionMode(true);
                          }
                        }}
                        sx={{ minHeight: 36, borderRadius: 2, px: 1.1, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}
                      >
                        {bulkDeleteSelectionMode ? "Cancelar selección" : "Seleccionar varios"}
                      </Button>
                    </>
                  ) : null}
                  {canCreateInView ? (
                    salesView ? (
                      <Button
                        data-wp-tutorial-target="sale-create"
                        size="small"
                        variant="contained"
                        startIcon={<AddRoundedIcon fontSize="small" />}
                        onClick={() => { setCreationLaunch({ mode: "normal", kind: "sale" }); setFormOpen(true); }}
                        sx={{ minHeight: 36, borderRadius: 2, px: 1.35, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}
                      >
                        Venta
                      </Button>
                    ) : (
                      <>
                        {/* [WP-STABLE][MOVEMENT-QUICK-ENTRY]
                            STATUS: IMPLEMENTED / STATIC_PASS_TARGET / BUILD_PENDING. Accesos rápidos
                            al mismo MovementFormDialog: tutorial guiado, venta de mostrador y compra preseleccionada.
                            Venta de mostrador usa un modo de captura rápida sin alterar payload ni endpoints. */}
                        <Button
                          data-wp-tutorial-target="movement-create"
                          size="small"
                          variant="contained"
                          onClick={() => { setCreationLaunch({ mode: "tutorial", kind: "" }); setFormOpen(true); }}
                          sx={{ minHeight: 36, borderRadius: 2, px: 1.35, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}
                        >
                          Tutorial
                        </Button>
                        {canCreateSale ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => { setCreationLaunch({ mode: "counter_sale", kind: "sale" }); setFormOpen(true); }}
                            sx={{ minHeight: 36, borderRadius: 2, px: 1.2, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}
                          >
                            Venta de mostrador
                          </Button>
                        ) : null}
                        {canCreatePurchase ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => { setCreationLaunch({ mode: "normal", kind: "purchase" }); setFormOpen(true); }}
                            sx={{ minHeight: 36, borderRadius: 2, px: 1.2, textTransform: "none", fontWeight: 950, whiteSpace: "nowrap" }}
                          >
                            Compré mercadería
                          </Button>
                        ) : null}
                      </>
                    )
                  ) : null}
                </Stack>
              )}
              activeFilterCount={[
                !salesView && filters.kind,
                filters.status,
                superAdmin && filters.scope,
                filters.dateFrom,
                filters.dateTo,
              ].filter(Boolean).length}
              onClear={() => {
                setFilters((current) => ({
                  ...current,
                  kind: salesView ? "sale" : "",
                  status: "",
                  scope: "",
                  dateFrom: "",
                  dateTo: "",
                }));
                setPage(0);
              }}
              drawerTitle="Filtrar movimientos"
              mobileActionsBelow
              sx={{ gap: 0.65 }}
            >
              <TextField
                select
                size="small"
                label="Orden"
                value={filters.sort}
                onChange={(event) => setFilter("sort", event.target.value)}
                sx={{ minWidth: { sm: 148 } }}
              >
                <MenuItem value="created_desc">Más nuevos</MenuItem>
                <MenuItem value="created_asc">Más antiguos</MenuItem>
              </TextField>
              <TextField select size="small" label="Tipo" value={filters.kind} onChange={(event) => setFilter("kind", event.target.value)} disabled={salesView} sx={{ minWidth: { sm: 140 } }}>
                <MenuItem value="">Todos</MenuItem>
                {KIND_OPTIONS.map((kind) => <MenuItem key={kind} value={kind}>{MOVEMENT_KIND_LABELS[kind]}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Estado" value={filters.status} onChange={(event) => setFilter("status", event.target.value)} sx={{ minWidth: { sm: 122 } }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="draft">Borrador</MenuItem>
                <MenuItem value="confirmed">Confirmado</MenuItem>
                <MenuItem value="voided">Anulado</MenuItem>
              </TextField>
              {superAdmin ? (
                <TextField select size="small" label="Alcance" value={filters.scope} onChange={(event) => setFilter("scope", event.target.value)} sx={{ minWidth: { sm: 132 } }}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="live">Operativos</MenuItem>
                  <MenuItem value="historical_reference">Históricos</MenuItem>
                </TextField>
              ) : null}
              <TextField size="small" type="date" label="Desde" value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" type="date" label="Hasta" value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} />
            </ResponsiveListControls>
          </Box>
        ) : null}

        {loading ? <ErpLoading label="Cargando movimientos..." /> : error ? null : !movements.length ? (
          hasActiveRefinement ? (
            <ErpEmpty
              title={salesView ? "No encontramos ventas con esta búsqueda" : "No encontramos movimientos con esta búsqueda"}
              description="Probá con otro término o volvé a ver todos los registros."
              action={<Button variant="contained" size="small" onClick={resetListView}>Ver todos</Button>}
            />
          ) : salesView ? (
            <ErpEmpty
              title="Todavía no hay ventas registradas"
              description="Cuando registres una venta desde Gestión, aparecerá acá sin mezclarla con los pedidos recibidos desde la web."
              action={canCreateInView ? <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={() => setFormOpen(true)}>Registrar venta</Button> : null}
            />
          ) : (
            <MovementFirstUseExperience
              canCreate={canCreateInView}
              canCreateItems={canCreateItems}
              canManageCount={canManageInitialCount}
              canManageRegisters={canManageRegisters}
              bootstrap={bootstrap}
              onCreate={() => setFormOpen(true)}
              onItems={() => navigate(`/dashboard/proyectos/${projectId}/sistema/items?inicio=1`)}
              onCount={() => navigate(`/dashboard/proyectos/${projectId}/sistema/stock?conteo=1`)}
              onCash={() => navigate(`/dashboard/proyectos/${projectId}/sistema/caja?crear=1`)}
            />
          )
        ) : (
          <>
            <Box sx={{ display: { xs: "block", md: "none" }, py: .15 }}>
              {(historicalSelectionMode && eligibleHistoricalOnPage.length) || (bulkDeleteSelectionMode && eligibleDeleteOnPage.length) ? (
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ px: 1, py: .65 }}>
                  <Stack direction="row" spacing={.45} alignItems="center">
                    <Checkbox
                      size="small"
                      checked={historicalSelectionMode ? allEligibleOnPageSelected : allDeleteOnPageSelected}
                      indeterminate={historicalSelectionMode
                        ? selectedEligibleOnPage.length > 0 && !allEligibleOnPageSelected
                        : selectedDeleteOnPage.length > 0 && !allDeleteOnPageSelected}
                      onChange={historicalSelectionMode ? toggleHistoricalPage : toggleBulkDeletePage}
                      inputProps={{ "aria-label": "Seleccionar movimientos de esta página" }}
                    />
                    <Typography sx={{ fontSize: ".72rem", fontWeight: 850, color: ERP_BRAND.muted }}>Seleccionar esta página</Typography>
                  </Stack>
                  {(historicalSelectionMode ? selectedHistoricalIds.length : selectedDeleteIds.length) ? (
                    <Typography sx={{ fontSize: ".72rem", fontWeight: 950 }}>
                      {historicalSelectionMode ? selectedHistoricalIds.length : selectedDeleteIds.length} seleccionados
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}
              {movements.map((movement) => (
                <MobileMovementCard
                  key={entityId(movement)}
                  movement={movement}
                  historicalSelectionMode={historicalSelectionMode}
                  bulkDeleteSelectionMode={bulkDeleteSelectionMode}
                  selected={historicalSelectionMode
                    ? selectedHistoricalSet.has(entityId(movement))
                    : selectedDeleteSet.has(entityId(movement))}
                  canUpdate={canUpdate}
                  canConfirm={canConfirm}
                  canVoid={canVoid}
                  canHardDelete={ownerCanDelete}
                  onToggleHistorical={toggleHistoricalSelection}
                  onToggleBulkDelete={toggleBulkDeleteSelection}
                  onView={setDetailId}
                  onEdit={(item) => { setEditing(item); setFormOpen(true); }}
                  onConfirm={(item) => setAction({ type: "confirm", movement: item })}
                  onVoid={(item) => setAction({ type: "void", movement: item })}
                  onHardDelete={(item) => setAction({ type: "hard_delete", movement: item })}
                />
              ))}
            </Box>

            <TableContainer sx={{ display: { xs: "none", md: "block" }, maxWidth: "100%", overflowX: "auto" }}>
              <ResponsiveOperationalTable
                size="small"
                mobileLabel={salesView ? "Ventas registradas" : "Movimientos"}
                mobileMode="scroll"
                sx={{
                  minWidth: 940,
                  "& .MuiTableCell-root": { py: 0.5, px: 1, verticalAlign: "middle", fontSize: ".76rem" },
                  "& .MuiTableHead-root .MuiTableCell-root": { py: 0.58, fontSize: ".69rem", fontWeight: 950 },
                  "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color .16s ease, box-shadow .16s ease" },
                  "& .MuiTableBody-root .MuiTableRow-root:hover": { bgcolor: "rgba(243,90,58,.035)", boxShadow: "inset 3px 0 0 rgba(243,90,58,.75)" },
                  "& .wp-movements-number-cell": {
                    position: { xs: "sticky", md: "static" },
                    left: 0,
                    zIndex: { xs: 2, md: "auto" },
                    bgcolor: "var(--wp-panel-surface, #fff)",
                    boxShadow: { xs: "6px 0 12px rgba(0,0,0,.06)", md: "none" },
                  },
                  "& thead .wp-movements-number-cell": { zIndex: { xs: 3, md: "auto" } },
                  "& .wp-movements-actions-cell": {
                    position: "sticky",
                    right: 0,
                    zIndex: 3,
                    minWidth: 116,
                    bgcolor: "var(--wp-panel-surface, #fff)",
                    boxShadow: "-8px 0 14px rgba(0,0,0,.07)",
                    whiteSpace: "nowrap",
                  },
                  "& thead .wp-movements-actions-cell": {
                    zIndex: 6,
                    bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {historicalSelectionMode || bulkDeleteSelectionMode ? (
                      <TableCell padding="checkbox" sx={{ width: 44 }}>
                        <Checkbox
                          size="small"
                          checked={historicalSelectionMode ? allEligibleOnPageSelected : allDeleteOnPageSelected}
                          indeterminate={historicalSelectionMode
                            ? selectedEligibleOnPage.length > 0 && !allEligibleOnPageSelected
                            : selectedDeleteOnPage.length > 0 && !allDeleteOnPageSelected}
                          disabled={historicalSelectionMode ? !eligibleHistoricalOnPage.length : !eligibleDeleteOnPage.length}
                          onChange={historicalSelectionMode ? toggleHistoricalPage : toggleBulkDeletePage}
                          inputProps={{ "aria-label": "Seleccionar movimientos de esta página" }}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell className="wp-movements-number-cell" sx={{ minWidth: 138 }}>Movimiento</TableCell>
                    <TableCell sx={{ minWidth: 138 }}>Creado</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Tipo</TableCell>
                    <TableCell sx={{ minWidth: 205 }}>Descripción</TableCell>
                    <TableCell sx={{ minWidth: 145 }}>Tercero</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Pendiente</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right" className="wp-movements-actions-cell">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.map((movement) => (
                    <TableRow
                      key={entityId(movement)}
                      hover
                      selected={historicalSelectionMode
                        ? selectedHistoricalSet.has(entityId(movement))
                        : selectedDeleteSet.has(entityId(movement))}
                    >
                      {historicalSelectionMode || bulkDeleteSelectionMode ? (
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={historicalSelectionMode
                              ? selectedHistoricalSet.has(entityId(movement))
                              : selectedDeleteSet.has(entityId(movement))}
                            disabled={historicalSelectionMode
                              ? !canMoveToHistorical(movement)
                              : movement.kind === "reversal" || !["draft", "confirmed", "voided"].includes(movement.status)}
                            onChange={() => (historicalSelectionMode
                              ? toggleHistoricalSelection(entityId(movement))
                              : toggleBulkDeleteSelection(entityId(movement)))}
                            inputProps={{ "aria-label": `Seleccionar ${movement.movementNumber}` }}
                          />
                        </TableCell>
                      ) : null}
                      <TableCell className="wp-movements-number-cell">
                        <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, fontSize: ".82rem", lineHeight: 1.2 }}>{movement.movementNumber}</Typography>
                        <Typography variant="caption" noWrap sx={{ display: "block", color: ERP_BRAND.muted, fontSize: ".64rem" }}>Op. {formatDateTime(movement.occurredAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography noWrap sx={{ fontWeight: 850, color: ERP_BRAND.dark, fontSize: ".78rem" }}>{formatDateTime(movement.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.35} alignItems="flex-start">
                          <Chip size="small" label={MOVEMENT_KIND_LABELS[movement.kind] || movement.kind} sx={{ height: 21, fontWeight: 850, "& .MuiChip-label": { px: 0.75, fontSize: ".68rem" } }} />
                          {movement.operationalScope === "historical_reference" ? (
                            <Chip size="small" icon={<HistoryRoundedIcon />} label="Histórico" variant="outlined" sx={{ height: 20, fontWeight: 900, "& .MuiChip-label": { px: 0.65, fontSize: ".64rem" }, "& .MuiChip-icon": { fontSize: 14 } }} />
                          ) : null}
                          {pendingPurchaseReceiptUnits(movement) > 0 ? (
                            <Chip size="small" color="warning" variant="outlined" label={`${pendingPurchaseReceiptUnits(movement)} en tránsito`} sx={{ height: 20, "& .MuiChip-label": { px: 0.7, fontSize: ".65rem" } }} />
                          ) : null}
                          {unidentifiedPurchaseReceiptUnits(movement) > 0 ? (
                            <Chip size="small" color="warning" label={`${unidentifiedPurchaseReceiptUnits(movement)} por identificar`} sx={{ height: 20, "& .MuiChip-label": { px: 0.7, fontSize: ".65rem" } }} />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography noWrap sx={{ fontWeight: 800, fontSize: ".79rem" }}>{movement.description || "—"}</Typography>
                        {movement.sourceNumber ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontSize: ".64rem" }}>Ref. {movement.sourceNumber}</Typography> : null}
                      </TableCell>
                      <TableCell><Typography noWrap sx={{ fontSize: ".78rem" }}>{movement.party?.name || "—"}</Typography></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 950, whiteSpace: "nowrap" }}>{formatMoney(movement.total, movement.currency)}</TableCell>
                      <TableCell align="right" sx={{ color: Number(movement.balanceDue) > 0 ? "warning.main" : ERP_BRAND.muted, fontWeight: 900, whiteSpace: "nowrap" }}>
                        {formatMoney(movement.balanceDue, movement.currency)}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={movement.status === "draft" ? {
                            display: "inline-flex",
                            borderRadius: 999,
                            "@keyframes wpMovementStatusAttention": {
                              "0%, 100%": { boxShadow: "0 0 0 0 rgba(243,90,58,.14)" },
                              "50%": { boxShadow: "0 0 0 5px rgba(243,90,58,0)" },
                            },
                            animation: "wpMovementStatusAttention 2.05s ease-in-out infinite",
                            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                          } : { display: "inline-flex" }}
                        >
                          <MovementStatusChip status={movement.status} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" className="wp-movements-actions-cell">
                        <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => setDetailId(entityId(movement))}><VisibilityRoundedIcon fontSize="small" /></IconButton></Tooltip>
                        {movement.status === "draft" && (canUpdate || canConfirm) ? (
                          <>
                            {canUpdate ? <Tooltip title="Editar borrador"><IconButton size="small" onClick={() => { setEditing(movement); setFormOpen(true); }}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip> : null}
                            {ownerCanDelete ? <Tooltip title="Eliminar borrador cargado por error"><IconButton size="small" color="error" onClick={() => setAction({ type: "owner_remove", movement })}><DeleteForeverRoundedIcon fontSize="small" /></IconButton></Tooltip> : null}
                            {canConfirm ? (
                              <Tooltip title="Confirmar movimiento">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => setAction({ type: "confirm", movement })}
                                  sx={{
                                    "@keyframes wpMovementConfirmNudge": {
                                      "0%, 100%": { transform: "scale(1)" },
                                      "50%": { transform: "scale(1.12)" },
                                    },
                                    animation: "wpMovementConfirmNudge 1.8s ease-in-out infinite",
                                    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                                  }}
                                >
                                  <CheckCircleRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </>
                        ) : null}
                        {movement.status === "confirmed" && movement.operationalScope !== "historical_reference" && canVoid ? (
                          <Tooltip title="Anular y conservar auditoría"><IconButton size="small" color="warning" onClick={() => setAction({ type: "void", movement })}><UndoRoundedIcon fontSize="small" /></IconButton></Tooltip>
                        ) : null}
                        {ownerCanDelete && movement.kind !== "reversal" && ["confirmed", "voided"].includes(movement.status) ? (
                          <Tooltip title="Eliminar definitivamente"><IconButton size="small" color="error" onClick={() => setAction({ type: "hard_delete", movement })}><DeleteForeverRoundedIcon fontSize="small" /></IconButton></Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveOperationalTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
              rowsPerPageOptions={[8, 12, 20, 40]}
              labelRowsPerPage="Filas"
              sx={{ display: { xs: "none", md: "block" }, minHeight: 42, "& .MuiTablePagination-toolbar": { minHeight: 42 }, "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { mb: 0, fontSize: ".73rem" } }}
            />
            <MobileListPagination
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              label={salesView ? "ventas" : "movimientos"}
              sx={{ display: { xs: "block", md: "none" }, borderTop: `1px solid ${ERP_BRAND.border}` }}
            />
          </>
        )}
      </Paper>

      <MovementFormDialog
        open={formOpen}
        onClose={closeForm}
        onSaved={async (savedMovement) => {
          const savedKind = String(savedMovement?.kind || initialKind || "");
          if (savedKind === "sale") {
            reportActiveDashboardTutorialMetric("movement_sale_completed", {
              operationId: String(savedMovement?.id || savedMovement?._id || `sale:${Date.now()}`),
              movementKind: savedKind,
              itemCount: Array.isArray(savedMovement?.items) ? savedMovement.items.length : 0,
            });
          } else if (savedKind === "purchase") {
            reportActiveDashboardTutorialMetric("movement_purchase_completed", {
              operationId: String(savedMovement?.id || savedMovement?._id || `purchase:${Date.now()}`),
              movementKind: savedKind,
              itemCount: Array.isArray(savedMovement?.items) ? savedMovement.items.length : 0,
            });
          }
          // [WP-STABLE][MOVEMENT-POST-SAVE-REFRESH]
          // STATUS: STATIC_PASS / STAGING_PENDING. El movimiento ya fue persistido;
          // una lectura auxiliar que falle no debe transformarse en un rechazo async
          // sin manejar ni hacer parecer que la operación principal se perdió.
          await Promise.allSettled([load(), refreshBootstrap()]);
        }}
        projectId={projectId}
        movement={editing}
        bootstrap={bootstrap}
        access={access}
        initialKind={creationLaunch.kind || initialKind}
        initialCreationMode={creationLaunch.mode}
        initialProductId={initialProductId}
        initialPreset={initialPreset}
        entryContext={entryContext}
      />

      <MovementDetailDialog
        open={Boolean(detailId)}
        onClose={closeDetail}
        projectId={projectId}
        movementId={detailId}
        canReadCustomers={canReadCustomers && membershipsEnabled}
        canReceiveStock={hasProjectPermission(access, "erp.stock.manage")}
        onStockReceived={async () => {
          await Promise.all([load(), refreshBootstrap()]);
        }}
      />

      <Dialog open={Boolean(action)} onClose={processing ? undefined : () => setAction(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>
          {action?.type === "confirm" ? "Confirmar movimiento" : action?.type === "owner_remove" ? "Corregir / eliminar movimiento" : action?.type === "hard_delete" ? "Eliminar definitivamente" : "Anular movimiento"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {action?.type === "confirm" ? (
            <Alert severity="warning">
              Al confirmar se aplicarán stock, asientos contables, transacciones y caja. Luego no podrá editarse; cualquier corrección se realiza mediante una reversa.
            </Alert>
          ) : action?.type === "hard_delete" ? (
            <Stack spacing={2}>
              <Alert severity="error">
                Esta acción elimina de raíz el movimiento y sus impactos ERP vinculados: stock, caja, transacciones, asientos y auditorías operativas derivadas. No crea una reversa y no se puede deshacer.
              </Alert>
              <Alert severity="warning">
                Si existe un comprobante fiscal activo o el stock está gobernado por un pedido, WebProfit bloqueará el borrado. El costo vigente del catálogo no se rebobina automáticamente para no pisar ajustes manuales posteriores.
              </Alert>
              <TextField
                fullWidth
                required={action?.movement?.status !== "draft"}
                multiline
                minRows={2}
                label={action?.movement?.status === "draft" ? "Motivo (opcional)" : "¿Qué se cargó mal?"}
                value={actionReason}
                onChange={(event) => setActionReason(event.target.value)}
              />
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Checkbox
                  checked={hardDeleteAccepted}
                  onChange={(event) => setHardDeleteAccepted(event.target.checked)}
                  inputProps={{ "aria-label": "Confirmar eliminación definitiva" }}
                  sx={{ p: .25, mt: .05 }}
                />
                <Typography sx={{ fontSize: ".82rem", fontWeight: 850, color: ERP_BRAND.dark }}>
                  Entiendo que este movimiento desaparecerá de la base operativa y que esta acción es irreversible.
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">Disponible sólo para el propietario del proyecto o soporte superadmin.</Typography>
            </Stack>
          ) : action?.type === "owner_remove" ? (
            <Stack spacing={2}>
              <Alert severity={action?.movement?.status === "draft" ? "info" : "warning"}>
                {action?.movement?.status === "draft"
                  ? "Este borrador todavía no impactó stock, caja ni contabilidad y se puede eliminar directamente."
                  : "Si no está facturado, WebProfit genera la reversa necesaria y lo saca de la vista operativa conservando auditoría. Si ya tiene comprobante fiscal, no permitirá borrarlo: primero requiere su corrección fiscal / nota de crédito."}
              </Alert>
              <TextField
                fullWidth
                required={action?.movement?.status !== "draft"}
                multiline
                minRows={2}
                label={action?.movement?.status === "draft" ? "Motivo (opcional)" : "¿Qué se cargó mal?"}
                value={actionReason}
                onChange={(event) => setActionReason(event.target.value)}
              />
              <Typography variant="caption" color="text.secondary">Esta acción sólo está disponible para el dueño del proyecto. Los accesos del equipo no pueden eliminar movimientos.</Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Alert severity="warning">La anulación genera un movimiento inverso para conservar la trazabilidad.</Alert>
              <TextField fullWidth required multiline minRows={3} label="Motivo de la anulación" value={actionReason} onChange={(event) => setActionReason(event.target.value)} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, "& .MuiButton-root": { minHeight: 44 } }}>
          <Button onClick={() => setAction(null)} disabled={processing}>Cancelar</Button>
          <Button
            variant="contained"
            color={action?.type === "confirm" ? "success" : "error"}
            onClick={performAction}
            disabled={processing || (action?.type === "hard_delete" && !hardDeleteAccepted)}
            startIcon={processing ? <WebProfitSpinner size={16} /> : undefined}
          >
            {processing ? "Procesando..." : action?.type === "confirm" ? "Confirmar" : action?.type === "owner_remove" ? (action?.movement?.status === "draft" ? "Eliminar borrador" : "Corregir y retirar") : action?.type === "hard_delete" ? "Eliminar de raíz" : "Generar reversa"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteDialogOpen} onClose={bulkDeleteProcessing ? undefined : () => setBulkDeleteDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Eliminar movimientos seleccionados</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.5}>
            <Alert severity="error">
              Vas a eliminar de raíz <strong>{selectedDeleteIds.length}</strong> movimiento{selectedDeleteIds.length === 1 ? "" : "s"}. WebProfit procesará cada uno de forma individual y conservará seleccionados los que el backend bloquee por reglas fiscales u operativas.
            </Alert>
            <Alert severity="warning">
              No se crean reversas. Esta acción es irreversible y puede modificar stock, caja, transacciones y contabilidad de cada movimiento eliminado.
            </Alert>
            <TextField
              fullWidth
              required
              multiline
              minRows={2}
              label="Motivo de la eliminación múltiple"
              value={bulkDeleteReason}
              onChange={(event) => setBulkDeleteReason(event.target.value)}
            />
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Checkbox
                checked={bulkDeleteAccepted}
                onChange={(event) => setBulkDeleteAccepted(event.target.checked)}
                inputProps={{ "aria-label": "Confirmar eliminación múltiple definitiva" }}
                sx={{ p: .25, mt: .05 }}
              />
              <Typography sx={{ fontSize: ".82rem", fontWeight: 850, color: ERP_BRAND.dark }}>
                Entiendo que los movimientos seleccionados desaparecerán de la base operativa y que esta acción no se puede deshacer.
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, "& .MuiButton-root": { minHeight: 44 } }}>
          <Button onClick={() => setBulkDeleteDialogOpen(false)} disabled={bulkDeleteProcessing}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={bulkDeleteProcessing ? <WebProfitSpinner size={16} /> : <DeleteForeverRoundedIcon />}
            onClick={hardDeleteSelectedMovements}
            disabled={bulkDeleteProcessing || !bulkDeleteAccepted || !bulkDeleteReason.trim() || !selectedDeleteIds.length}
            sx={{ textTransform: "none", fontWeight: 950 }}
          >
            {bulkDeleteProcessing ? "Eliminando…" : `Eliminar ${selectedDeleteIds.length} de raíz`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historicalDialogOpen} onClose={historicalProcessing ? undefined : () => setHistoricalDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Pasar movimientos a histórico</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.5}>
            <Alert severity="warning">
              Esta herramienta es sólo para migración. Conserva la factura y su trazabilidad, la separa de la operación actual y neutraliza el stock que haya aplicado mediante un ajuste ERP auditado.
            </Alert>
            <Typography sx={{ fontSize: ".84rem", color: ERP_BRAND.muted }}>
              Seleccionaste <strong>{selectedHistoricalIds.length}</strong> movimiento{selectedHistoricalIds.length === 1 ? "" : "s"}. Usalo para comprobantes anteriores al punto de partida real del negocio, no para corregir operaciones actuales.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Motivo / referencia de migración"
              value={historicalReason}
              onChange={(event) => setHistoricalReason(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setHistoricalDialogOpen(false)} disabled={historicalProcessing}>Cancelar</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={historicalProcessing ? <WebProfitSpinner size={16} /> : <HistoryRoundedIcon />}
            onClick={migrateSelectedHistorical}
            disabled={historicalProcessing || !selectedHistoricalIds.length}
            sx={{ textTransform: "none", fontWeight: 950 }}
          >
            {historicalProcessing ? "Migrando…" : "Confirmar histórico"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
